import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  Event as EventIcon,
  Group as GroupIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  QueueOutlined as QueueIcon,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../context/AuthContext';
import { groupPlayAPI, tenantSettingsAPI, amenityAPI } from '../../services/api';
import { GroupPlay, GroupPlayInstance, GroupPlayBooking, GroupPlayBookingStatus, UserRole } from '../../types';
import { formatDate, formatTimeString, formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import RegisterMemberToGroupPlayDialog from './RegisterMemberToGroupPlayDialog';
import ReservationResultsDialog from '../coaching/ReservationResultsDialog';

const ViewGroupPlayInstancePage: React.FC = () => {
  const { groupPlayId, instanceId } = useParams<{ groupPlayId: string; instanceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Determine referrer for breadcrumbs - check location state or fallback
  const referrer = (location.state as any)?.from || 'group-play-management';

  // State management
  const [groupPlayInstance, setGroupPlayInstance] = useState<GroupPlayInstance | null>(null);
  const [groupPlay, setGroupPlay] = useState<GroupPlay | null>(null);
  const [bookings, setBookings] = useState<GroupPlayBooking[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [showCancelBookingDialog, setShowCancelBookingDialog] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [showRegisterDialog, setShowRegisterDialog] = useState<boolean>(false);
  const [showCapacityIncreaseDialog, setShowCapacityIncreaseDialog] = useState<boolean>(false);
  const [pendingCapacityChange, setPendingCapacityChange] = useState<number | null>(null);
  const [waitlistCount, setWaitlistCount] = useState<number>(0);
  const [rebooking, setRebooking] = useState<boolean>(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState<boolean>(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);

  // Cancellation window state
  const [cancellationWindowHours, setCancellationWindowHours] = useState(24);
  const [hoursUntilBooking, setHoursUntilBooking] = useState<number | null>(null);
  const [canCancelWithoutPenalty, setCanCancelWithoutPenalty] = useState(false);
  const [adminOverride, setAdminOverride] = useState(false);

  // Missing amenity bookings state
  const [missingVenues, setMissingVenues] = useState<string[]>([]);
  const missingBookingsControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight missing-bookings check on unmount
  useEffect(() => () => { missingBookingsControllerRef.current?.abort(); }, []);

  // Check if user is admin
  const isAdmin = user && [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.COACH].includes(user.role);

  // Helper function to get breadcrumb path based on referrer
  const getBreadcrumbPath = () => {
    switch (referrer) {
      case 'group-play-details':
        return { label: 'Group Play Details', icon: <GroupIcon />, path: `/group-plays/${groupPlayId}` };
      case 'calendar':
        return { label: 'Calendar', icon: <EventIcon />, path: '/calendar' };
      case 'dashboard':
        return { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' };
      case 'group-play-management':
      default:
        return { label: 'Group Play Management', icon: <GroupIcon />, path: '/group-plays' };
    }
  };

  const breadcrumbPath = getBreadcrumbPath();

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    venues: [] as string[],
    capacity: 0,
    sessionDate: '',
    startTime: '',
    endTime: '',
    notes: ''
  });

  // Fetch instance data
  useEffect(() => {
    if (groupPlayId && instanceId) {
      fetchGroupPlayInstance();
      fetchGroupPlay();
      fetchTenantSettings();
    }
  }, [groupPlayId, instanceId]);

  // Calculate hours until booking when instance changes
  useEffect(() => {
    if (groupPlayInstance) {
      calculateHoursUntilSession();
    }
  }, [groupPlayInstance, cancellationWindowHours]);

  const fetchTenantSettings = async () => {
    try {
      const response = await tenantSettingsAPI.getAllSettings();
      const settings = response.data;
      const cancelHours = settings.cancelWithoutPenaltyHrs || 24;
      setCancellationWindowHours(cancelHours);
    } catch (error: any) {
      console.error('Error fetching tenant settings:', error);
      // Use default if fetch fails
      setCancellationWindowHours(24);
    }
  };

  const calculateHoursUntilSession = () => {
    if (!groupPlayInstance) return;

    try {
      const dateStr = groupPlayInstance.sessionDate;
      const timeStr = groupPlayInstance.startTime;
      
      if (!dateStr || !timeStr) {
        setHoursUntilBooking(null);
        setCanCancelWithoutPenalty(false);
        return;
      }
      
      // Parse the date and time
      const [year, month, day] = dateStr.split('-').map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);
      
      // Create session date in local timezone
      const sessionDate = new Date(year, month - 1, day, hours, minutes, 0);
      const now = new Date();
      
      // Calculate difference in milliseconds
      const diffMs = sessionDate.getTime() - now.getTime();
      
      // Convert to hours
      const diffHours = diffMs / (1000 * 60 * 60);
      
      setHoursUntilBooking(diffHours);
      setCanCancelWithoutPenalty(diffHours >= cancellationWindowHours);
    } catch (error) {
      console.error('Error calculating hours until session:', error);
      setHoursUntilBooking(null);
      setCanCancelWithoutPenalty(false);
    }
  };

  const fetchGroupPlay = async () => {
    if (!groupPlayId) return;

    try {
      const response = await groupPlayAPI.getGroupPlayById(groupPlayId);
      setGroupPlay(response.data);
    } catch (error: any) {
      console.error('Error fetching group play:', error);
      // Don't set error state as this is not critical
    }
  };

  const fetchGroupPlayInstance = async () => {
    if (!groupPlayId || !instanceId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await groupPlayAPI.getGroupPlayInstanceById(groupPlayId, instanceId);
      const instance = response.data;
      
      setGroupPlayInstance(instance);

      // Helper function to convert time string to HH:mm format for time inputs
      const formatTimeForInput = (timeString: string | null | undefined): string => {
        if (!timeString) return '';
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
        return timeString;
      };

      // Initialize edit form with current data
      setEditForm({
        name: instance.name || '',
        venues: instance.venues || [],
        capacity: instance.capacity || 0,
        sessionDate: formatDateForInput(parseDateOnlyString(instance.sessionDate)),
        startTime: formatTimeForInput(instance.startTime),
        endTime: formatTimeForInput(instance.endTime),
        notes: instance.notes || ''
      });

      // Fetch bookings for this instance
      fetchBookings();

      // Check for missing amenity bookings if admin
      if (isAdmin && instance.venues && instance.venues.length > 0) {
        checkMissingBookings(instance);
      }

    } catch (error: any) {
      console.error('Error fetching group play instance:', error);
      setError('Failed to load instance details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    if (!groupPlayId || !instanceId) return;

    try {
      const response = await groupPlayAPI.getGroupPlayInstanceBookings(groupPlayId, instanceId);
      setBookings(response.data || []);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
    }
  };

  const checkMissingBookings = async (instance: GroupPlayInstance) => {
    missingBookingsControllerRef.current?.abort();
    const controller = new AbortController();
    missingBookingsControllerRef.current = controller;

    try {
      const instances = [{
        id: instance.id,
        type: 'groupPlayInstance' as const,
        venues: instance.venues || [],
        sessionDate: instance.sessionDate,
        startTime: instance.startTime,
        endTime: instance.endTime
      }];

      const response = await amenityAPI.checkMissingBookings(instances, controller.signal);
      const results = response.data.results || [];
      
      if (results.length > 0 && results[0].missingVenues) {
        setMissingVenues(results[0].missingVenues);
      } else {
        setMissingVenues([]);
      }
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED') return;
      console.error('Error checking for missing bookings:', error);
      // Silently fail - this is a non-critical feature
      setMissingVenues([]);
    }
  };

  const handleSave = async () => {
    if (!groupPlayId || !instanceId || !groupPlayInstance) return;

    const newCapacity = parseInt(editForm.capacity.toString());
    const currentCapacity = groupPlayInstance.capacity;
    const confirmedCount = bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length;
    const currentWaitlistCount = bookings.filter(b => b.status === GroupPlayBookingStatus.WAITLIST).length;

    // Check if capacity is being decreased below current confirmed bookings
    if (newCapacity < confirmedCount) {
      setError(`Cannot decrease capacity to ${newCapacity}. There are currently ${confirmedCount} confirmed bookings. Please cancel some bookings before reducing capacity.`);
      return;
    }

    // Check if capacity is being increased and there are waitlisted members
    if (newCapacity > currentCapacity && currentWaitlistCount > 0) {
      const spotsToOpen = newCapacity - confirmedCount;
      const membersToPromote = Math.min(spotsToOpen, currentWaitlistCount);
      
      // Show confirmation dialog
      setPendingCapacityChange(newCapacity);
      setWaitlistCount(membersToPromote);
      setShowCapacityIncreaseDialog(true);
      return;
    }

    // No validation issues, proceed with save
    await performSave(newCapacity);
  };

  const performSave = async (capacity: number) => {
    if (!groupPlayId || !instanceId) return;

    try {
      setSaving(true);
      setError(null);

      const updateData = {
        name: editForm.name,
        venues: editForm.venues,
        capacity: capacity,
        sessionDate: editForm.sessionDate,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        notes: editForm.notes
      };

      const response = await groupPlayAPI.updateGroupPlayInstance(groupPlayId, instanceId, updateData);
      
      // Check if members were promoted from waitlist
      if (response.data.promotedMembers && response.data.promotedMembers.count > 0) {
        const promotedNames = response.data.promotedMembers.members.map((m: any) => m.memberName).join(', ');
        setError(null);
        // Show success message (you could add a success snackbar here)
        console.log(`${response.data.promotedMembers.count} member(s) promoted from waitlist: ${promotedNames}`);
      }
      
      // Venues/date/time changes re-reserve courts; surface the outcome
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityName: editForm.name,
          operationType: 'update',
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      }

      // Refresh the data
      await fetchGroupPlayInstance();
      await fetchBookings();
      setEditMode(false);

    } catch (error: any) {
      console.error('Error updating instance:', error);
      setError(error.response?.data?.message || 'Failed to update instance');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCapacityIncrease = async () => {
    setShowCapacityIncreaseDialog(false);
    if (pendingCapacityChange !== null) {
      await performSave(pendingCapacityChange);
      setPendingCapacityChange(null);
      setWaitlistCount(0);
    }
  };

  const handleCancelCapacityIncrease = () => {
    setShowCapacityIncreaseDialog(false);
    setPendingCapacityChange(null);
    setWaitlistCount(0);
  };

  const handleRebookVenues = async () => {
    if (!groupPlayId || !instanceId || !groupPlayInstance) return;

    if (!window.confirm('This will rebook all venues for this instance. Any existing venue bookings will be recreated. Continue?')) {
      return;
    }

    try {
      setRebooking(true);
      setError(null);

      // Call update with the current data to trigger venue rebooking
      const updateData = {
        name: groupPlayInstance.name,
        venues: groupPlayInstance.venues,
        capacity: groupPlayInstance.capacity,
        sessionDate: groupPlayInstance.sessionDate,
        startTime: groupPlayInstance.startTime,
        endTime: groupPlayInstance.endTime,
        notes: groupPlayInstance.notes,
        rebookVenues: true
      };

      const response = await groupPlayAPI.updateGroupPlayInstance(groupPlayId, instanceId, updateData);
      
      // Check if reservation result is available
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityName: groupPlayInstance.name,
          operationType: 'rebook',
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      }
      
      // Refresh data to check if bookings were created
      await fetchGroupPlayInstance();
      
    } catch (error: any) {
      console.error('Error rebooking venues:', error);
      setError(error.response?.data?.message || 'Failed to rebook venues');
    } finally {
      setRebooking(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset form to original values
    if (groupPlayInstance) {
      const formatTimeForInput = (timeString: string | null | undefined): string => {
        if (!timeString) return '';
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
        return timeString;
      };

      setEditForm({
        name: groupPlayInstance.name || '',
        venues: groupPlayInstance.venues || [],
        capacity: groupPlayInstance.capacity || 0,
        sessionDate: formatDateForInput(parseDateOnlyString(groupPlayInstance.sessionDate)),
        startTime: formatTimeForInput(groupPlayInstance.startTime),
        endTime: formatTimeForInput(groupPlayInstance.endTime),
        notes: groupPlayInstance.notes || ''
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return 'primary';
      case 'active':
        return 'success';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getBookingStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'waitlist':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleCancelBooking = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setAdminOverride(false);
    setCancelError(null);
    setShowCancelBookingDialog(true);
  };

  const getSelectedBooking = () => {
    return bookings.find(b => b.id === selectedBookingId);
  };

  const handleRegisterSuccess = async () => {
    // Refresh the data after successful registration
    await fetchGroupPlayInstance();
    await fetchBookings();
  };

  const confirmCancelBooking = async () => {
    if (!groupPlayId || !instanceId || !selectedBookingId) return;

    try {
      setCancelError(null);
      
      // Prepare request body for admin override
      const requestBody = adminOverride ? { overrideCancellationWindow: true } : {};

      const response = await groupPlayAPI.cancelGroupPlayInstanceBooking(
        groupPlayId, 
        instanceId, 
        selectedBookingId,
        requestBody
      );
      
      setShowCancelBookingDialog(false);
      setSelectedBookingId(null);
      setAdminOverride(false);
      
      // Refresh the data
      await fetchGroupPlayInstance();
      await fetchBookings();
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      const errorMessage = error.response?.data?.message || 'Failed to cancel booking';
      setCancelError(errorMessage);
    }
  };

  const handleCancelInstance = async () => {
    if (!groupPlayId || !instanceId) return;

    try {
      setCancelling(true);
      setCancelError(null);
      
      await groupPlayAPI.cancelGroupPlayInstance(groupPlayId, instanceId);
      
      setShowCancelDialog(false);
      
      // Refresh the data to show cancelled status
      await fetchGroupPlayInstance();
      
      // Navigate back after successful cancellation
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      console.error('Error cancelling instance:', error);
      
      // Check if error response contains registered members info
      if (error.response?.data?.registeredMembers) {
        setCancelError(
          error.response.data.error || 
          `Cannot cancel: ${error.response.data.registeredMembers.length} member(s) are registered`
        );
      } else {
        setCancelError(error.response?.data?.error || error.response?.data?.message || 'Failed to cancel instance');
      }
    } finally {
      setCancelling(false);
    }
  };

  const isInstanceInPast = (): boolean => {
    if (!groupPlayInstance) return false;
    
    // Use safe date parsing to avoid timezone conversion
    const sessionDate = parseDateOnlyString(groupPlayInstance.sessionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    sessionDate.setHours(0, 0, 0, 0);
    
    // If session date is in the past, it's definitely past
    if (sessionDate < today) {
      return true;
    }
    
    // If session date is today, check the time
    if (sessionDate.getTime() === today.getTime()) {
      const now = new Date();
      const [hours, minutes] = (groupPlayInstance.startTime || '00:00').split(':').map(Number);
      const sessionTime = new Date();
      sessionTime.setHours(hours, minutes, 0, 0);
      
      return now > sessionTime;
    }
    
    return false;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!groupPlayInstance) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Instance not found</Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          {referrer !== 'dashboard' && (
            <Button
              component={RouterLink}
              to={breadcrumbPath.path}
              color="inherit"
              startIcon={breadcrumbPath.icon}
              sx={{ textTransform: 'none' }}
            >
              {breadcrumbPath.label}
            </Button>
          )}
          {groupPlay && referrer !== 'group-play-details' && (
            <Button
              component={RouterLink}
              to={`/group-plays/${groupPlayId}`}
              color="inherit"
              startIcon={<GroupIcon />}
              sx={{ textTransform: 'none' }}
            >
              {groupPlay.name}
            </Button>
          )}
          <Typography color="text.primary">Instance Details</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GroupIcon sx={{ mr: 2, fontSize: 40 }} />
            <Box>
              <Typography variant="h4" component="h1">
                {groupPlayInstance.name}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {formatDate(groupPlayInstance.sessionDate)} at {formatTimeString(groupPlayInstance.startTime)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              Back
            </Button>
            {!editMode && (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'employee' || user?.role === 'coach') && (
              <>
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                  variant="contained"
                  disabled={isInstanceInPast()}
                >
                  Edit
                </Button>
                {groupPlayInstance.status !== 'CANCELLED' && (
                  <Button
                    startIcon={<CancelIcon />}
                    onClick={() => setShowCancelDialog(true)}
                    color="error"
                    variant="outlined"
                    disabled={isInstanceInPast()}
                  >
                    Cancel Instance
                  </Button>
                )}
              </>
            )}
            {editMode && (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  variant="contained"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  disabled={saving}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main Instance Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Instance Information
              </Typography>
              <Grid container spacing={3}>
                {editMode ? (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DatePicker
                        label="Session Date"
                        value={editForm.sessionDate ? parseDateOnlyString(editForm.sessionDate) : null}
                        onChange={(date) => setEditForm({ ...editForm, sessionDate: date ? formatDateForInput(date) : '' })}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Capacity"
                        type="number"
                        value={editForm.capacity}
                        onChange={(e) => setEditForm({ ...editForm, capacity: parseInt(e.target.value) || 0 })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Start Time"
                        type="time"
                        value={editForm.startTime}
                        onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="End Time"
                        type="time"
                        value={editForm.endTime}
                        onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Notes"
                        multiline
                        rows={3}
                        value={editForm.notes}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid size={{ xs: 12 }}>
                    <List disablePadding>
                      {/* Date & Time - Combined */}
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 3 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                            <CalendarIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Date</Typography>
                              <Typography variant="body2" color="text.secondary">{formatDate(groupPlayInstance.sessionDate)}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 auto', sm: '0 0 50%' } }}>
                            <ScheduleIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Time</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatTimeString(groupPlayInstance.startTime)} - {formatTimeString(groupPlayInstance.endTime)}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </ListItem>

                      {/* Venues */}
                      {groupPlayInstance.venueNames && groupPlayInstance.venueNames.length > 0 && (
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <LocationIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Venues</Typography>
                                {isAdmin && !editMode && groupPlayInstance.status === 'SCHEDULED' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleRebookVenues}
                                    disabled={rebooking}
                                    sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '0.7rem' }}
                                  >
                                    {rebooking ? 'Rebooking...' : 'Rebook'}
                                  </Button>
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {groupPlayInstance.venueNames.join(', ')}
                              </Typography>
                              {isAdmin && missingVenues.length > 0 && (
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <WarningIcon sx={{ fontSize: '1rem', color: '#ff9800' }} />
                                  <Typography variant="caption" sx={{ color: '#ff9800', fontWeight: 'medium' }}>
                                    {missingVenues.length} court{missingVenues.length > 1 ? 's' : ''} missing booking{missingVenues.length > 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </ListItem>
                      )}

                      {/* Type */}
                      {groupPlay?.type && (
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <EventIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Type</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {groupPlay.type.charAt(0).toUpperCase() + groupPlay.type.slice(1)}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      )}

                      {/* Capacity */}
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <PersonIcon sx={{ mr: 2, color: 'primary.main', fontSize: 20 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>Capacity</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length} / {groupPlayInstance.capacity}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={(bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length >= groupPlayInstance.capacity) ? 'Full' : 'Available'}
                              color={(bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length >= groupPlayInstance.capacity) ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </ListItem>

                      {/* Waitlist */}
                      {groupPlayInstance.isWaitlistAvailable && bookings.filter(b => b.status === GroupPlayBookingStatus.WAITLIST).length > 0 && (
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1, border: 1, borderColor: 'warning.light' }}>
                            <QueueIcon sx={{ mr: 2, color: 'warning.main', fontSize: 24 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>Waitlist</Typography>
                              <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 'medium' }}>
                                {bookings.filter(b => b.status === GroupPlayBookingStatus.WAITLIST).length} on waitlist
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      )}

                      {/* Notes */}
                      {groupPlayInstance.notes && (
                        <ListItem sx={{ px: 0, py: 1 }}>
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>Notes</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {groupPlayInstance.notes}
                            </Typography>
                          </Box>
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                )}
              </Grid>
            </Paper>

          </Grid>

          {/* Status & Bookings Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Capacity Status Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Capacity Status
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3" color="primary">
                    {bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {groupPlayInstance.capacity} spots filled
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Available: {Math.max(0, groupPlayInstance.capacity - bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length)}
                  </Typography>
                  <Typography variant="body2">
                    {Math.round((bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length / groupPlayInstance.capacity) * 100)}% full
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.300',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min((bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length / groupPlayInstance.capacity) * 100, 100)}%`,
                      height: '100%',
                      backgroundColor: (bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED).length / groupPlayInstance.capacity) >= 0.8 ? 'error.main' : 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
                {groupPlayInstance.isWaitlistAvailable && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Waitlist available when full ({bookings.filter(b => b.status === GroupPlayBookingStatus.WAITLIST).length} on waitlist)
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Bookings Card */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    Bookings ({bookings.length})
                  </Typography>
                  {(user?.role !== 'member') && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonIcon />}
                      onClick={() => setShowRegisterDialog(true)}
                    >
                      + Register
                    </Button>
                  )}
                </Box>
                
                {bookings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No bookings for this instance
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Member</TableCell>
                          <TableCell>Status</TableCell>
                          {(user?.role !== 'member') && (
                            <TableCell align="right">Actions</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: '0.875rem' }}>
                                  {(booking.memberName || booking.guestName || 'U').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">{booking.memberName || booking.guestName || 'Unknown'}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {booking.memberEmail || booking.guestEmail || 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={booking.status}
                                color={getBookingStatusColor(booking.status)}
                                size="small"
                              />
                            </TableCell>
                            {(user?.role !== 'member') && (
                              <TableCell align="right">
                                {booking.status === GroupPlayBookingStatus.CONFIRMED && (
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelBooking(booking.id)}
                                  >
                                    Cancel
                                  </Button>
                                )}
                                {booking.status === GroupPlayBookingStatus.WAITLIST && (
                                  <Button
                                    size="small"
                                    color="warning"
                                    onClick={() => handleCancelBooking(booking.id)}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Register Dialog - Note: For now showing a simple message. A proper GroupPlay registration dialog could be created */}
        <RegisterMemberToGroupPlayDialog
          open={showRegisterDialog}
          onClose={() => setShowRegisterDialog(false)}
          groupPlayInstance={groupPlayInstance}
          groupPlayId={groupPlayId!}
          onSuccess={handleRegisterSuccess}
        />

        {/* Capacity Increase Confirmation Dialog */}
        <Dialog
          open={showCapacityIncreaseDialog}
          onClose={handleCancelCapacityIncrease}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Capacity Increase</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are increasing the capacity from {groupPlayInstance?.capacity} to {pendingCapacityChange}.
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              There {waitlistCount === 1 ? 'is' : 'are'} currently <strong>{waitlistCount} member{waitlistCount !== 1 ? 's' : ''}</strong> on the waitlist.
            </Typography>
            <Typography variant="body1">
              {waitlistCount === 1 ? 'This member' : 'These members'} will be automatically promoted to confirmed status & receive email notification(s).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelCapacityIncrease} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmCapacityIncrease} 
              variant="contained" 
              color="primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Confirm & Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Instance Confirmation Dialog */}
        <Dialog 
          open={showCancelDialog} 
          onClose={() => !cancelling && setShowCancelDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cancel Instance</DialogTitle>
          <DialogContent>
            {cancelError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {cancelError}
              </Alert>
            ) : (
              <>
                <Typography gutterBottom>
                  Are you sure you want to cancel this group play instance?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This will:
                </Typography>
                <Box component="ul" sx={{ mt: 1 }}>
                  <li>
                    <Typography variant="body2">Mark the instance as cancelled</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Cancel all associated court bookings</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Create an audit log entry</Typography>
                  </li>
                </Box>
                {bookings.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <strong>Note:</strong> There are {bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED || b.status === GroupPlayBookingStatus.WAITLIST).length} registered member(s). 
                    You must cancel all bookings before cancelling the instance.
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                setShowCancelDialog(false);
                setCancelError(null);
              }}
              disabled={cancelling}
            >
              {cancelError ? 'Close' : 'No, Keep Instance'}
            </Button>
            {!cancelError && (
              <Button
                onClick={handleCancelInstance}
                color="error"
                variant="contained"
                disabled={cancelling || bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED || b.status === GroupPlayBookingStatus.WAITLIST).length > 0}
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Instance'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Cancel Booking Confirmation Dialog */}
        <Dialog 
          open={showCancelBookingDialog} 
          onClose={() => !cancelError && setShowCancelBookingDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon color={getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST ? 'warning' : 'error'} />
              {getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST ? 'Remove from Waitlist' : 'Cancel Booking'}
            </Box>
          </DialogTitle>
          <DialogContent>
            {cancelError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {cancelError}
              </Alert>
            )}

            <Typography variant="body1" gutterBottom>
              {getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST 
                ? 'Are you sure you want to remove this member from the waitlist?' 
                : 'Are you sure you want to cancel this booking?'}
            </Typography>

            {groupPlayInstance && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Session:</strong> {groupPlayInstance.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {formatDate(groupPlayInstance.sessionDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Time:</strong> {formatTimeString(groupPlayInstance.startTime)} - {formatTimeString(groupPlayInstance.endTime)}
                </Typography>
                {hoursUntilBooking !== null && hoursUntilBooking > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Hours Until Session:</strong> {hoursUntilBooking.toFixed(1)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Cancellation Window Warning - Only for confirmed bookings */}
            {getSelectedBooking()?.status !== GroupPlayBookingStatus.WAITLIST && hoursUntilBooking !== null && !canCancelWithoutPenalty && hoursUntilBooking > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Cancellation Window Alert
                </Typography>
                <Typography variant="body2">
                  Only {hoursUntilBooking.toFixed(1)} hours remaining until session.
                  {' '}Cancellation requires at least {cancellationWindowHours} hours notice.
                  {!isAdmin && ' Cancellation fees will apply - your transaction will not be cancelled.'}
                  {isAdmin && ' As an admin, you can override this restriction.'}
                </Typography>
              </Alert>
            )}

            {/* Success message for outside window - Only for confirmed bookings */}
            {getSelectedBooking()?.status !== GroupPlayBookingStatus.WAITLIST && canCancelWithoutPenalty && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This cancellation is outside the {cancellationWindowHours}-hour window. 
                  Your transaction will be cancelled and no fees will be charged.
                </Typography>
              </Alert>
            )}

            {/* Info message for waitlist removal */}
            {getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Removing this member from the waitlist will automatically promote the next person in line (if any). 
                  No charges apply as this member was on the waitlist.
                </Typography>
              </Alert>
            )}

            {/* Admin Override Checkbox - Only for confirmed bookings within cancellation window */}
            {getSelectedBooking()?.status !== GroupPlayBookingStatus.WAITLIST && isAdmin && !canCancelWithoutPenalty && hoursUntilBooking !== null && hoursUntilBooking > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={adminOverride}
                    onChange={(e) => setAdminOverride(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Typography variant="body2">
                    <strong>Admin Override:</strong> Cancel transaction despite being within cancellation window
                  </Typography>
                }
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowCancelBookingDialog(false);
              setCancelError(null);
              setAdminOverride(false);
            }}>
              {getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST ? 'Keep on Waitlist' : 'Keep Booking'}
            </Button>
            <Button 
              onClick={confirmCancelBooking} 
              color={getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST ? 'warning' : 'error'}
              variant="contained"
              disabled={getSelectedBooking()?.status !== GroupPlayBookingStatus.WAITLIST && !canCancelWithoutPenalty && !adminOverride}
            >
              {getSelectedBooking()?.status === GroupPlayBookingStatus.WAITLIST ? 'Remove from Waitlist' : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reservation Results Dialog */}
        {reservationDialogData && (
          <ReservationResultsDialog
            open={reservationDialogOpen}
            onClose={() => {
              setReservationDialogOpen(false);
              setReservationDialogData(null);
            }}
            entityType="groupPlay"
            entityName={reservationDialogData.entityName}
            operationType={reservationDialogData.operationType}
            reservationResult={reservationDialogData.reservationResult}
          />
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default ViewGroupPlayInstancePage;
